import { Helmet } from 'react-helmet';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as R from 'Router/routes';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import EmailIcon from 'assets/icons/ico-mail.svg?react';
import MobilePhoneIcon from 'assets/icons/ico-mobile.svg?react';
import IDCardIcon from 'assets/icons/ico-id.svg?react';
import BankIDIcon from 'assets/icons/ico-bankid.svg?react';
import InternetBankingIcon from 'assets/icons/ico-ib.svg?react';
import Box from 'components/Box/Box';
import styles from './Intro.module.scss';
import BankIDModal from './BankIDModal';
import InternetBankingModal from './InternetBankingModal';
import { Button, Link, Modal, Typography } from '../../../components';

type OpenedModal = 'bank_id' | 'internet_banking' | null;

function IntroPage() {
  const { t } = useTranslation(['intro', 'common']);

  const [openedModal, setOpenedModal] = useState<OpenedModal>(null);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      {/* <VideoBannerPortal
        classNameContainer={styles['video-banner-wrapper']}
        content={{
          title: 'intro:video_banner_title',
          description: 'intro:video_banner_description',
          link: 'intro:video_banner_link',
        }}
      /> */}

      <Typography className={styles.title} kind="h1" size="xl" fontWeight={700}>
        {t('title')}
      </Typography>

      <section>
        <Typography fontWeight={600} className={styles.customer}>
          {t('customer_title')}
        </Typography>
        <Typography className={styles.customer} fontWeight={400} size="s">
          {t('customer_message')}
          {' – '}
          <Link
            className={styles.link}
            external
            to={import.meta.env.VITE_DASHBOARD_URL}
          >
            {t('login')}
          </Link>
        </Typography>
      </section>

      <section>
        <Typography className={styles.info} kind="h3" size="m">
          {t('info')}
        </Typography>

        <Box className={styles.card}>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <IDCardIcon className={styles.icon} />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
            >
              {t('id')}
            </Typography>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <MobilePhoneIcon className={styles.icon} />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
            >
              {t('mobile')}
            </Typography>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <EmailIcon className={styles.icon} />
            </div>
            <Typography
              fontWeight={500}
              className={styles['info-container-font']}
            >
              {t('email')}
            </Typography>
          </div>
        </Box>
      </section>

      <section>
        <Typography className={styles.info} kind="h3" size="m">
          {t('info_optional')}
        </Typography>

        <Box className={styles.card}>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <BankIDIcon />
            </div>
            <div className={styles.column}>
              <Typography
                fontWeight={500}
                className={styles['info-container-font']}
              >
                {t('bank_id')}
              </Typography>
              <Link
                dataTest="read-more-bank"
                className={styles['more-information']}
                onClick={() => setOpenedModal('bank_id')}
              >
                {t('more_information')}
              </Link>
            </div>
          </div>
          <div className={styles['card-box']}>
            <div className={styles['icon-container']}>
              <InternetBankingIcon />
            </div>
            <div className={styles.column}>
              <Typography
                fontWeight={500}
                className={styles['info-container-font']}
              >
                {t('online_banking_access')}
              </Typography>
              <Link
                dataTest="read-more-bank"
                className={styles['more-information']}
                onClick={() => setOpenedModal('internet_banking')}
              >
                {t('more_information')}
              </Link>
            </div>
          </div>
        </Box>
      </section>

      <section className={styles.cntBtnWrapper}>
        <Button
          dataTest="continue"
          className={styles.continue}
          to={R.PERSONAL_DETAILS}
          type="submit"
        >
          {t('common:continue')}
        </Button>

        <div>
          <Link
            dataTest="back"
            to={`${import.meta.env.VITE_HOMEPAGE_URL}`}
            external
          >
            <ChevronIcon className={styles.chevron} />
            {t('common:back')}
          </Link>
        </div>
      </section>

      {openedModal === 'bank_id' && (
        <Modal className={styles.modal} onClose={() => setOpenedModal(null)}>
          <BankIDModal />
        </Modal>
      )}

      {openedModal === 'internet_banking' && (
        <Modal className={styles.modal} onClose={() => setOpenedModal(null)}>
          <InternetBankingModal />
        </Modal>
      )}
    </>
  );
}

export default IntroPage;
